/**
 * A copy & paste from react-reformed ValidateSchema middleware.
 */
import React from "react";
import hoistNonReactStatics from "hoist-non-react-statics";
import assign from "object-assign";
import { observer } from "mobx-react";

const getComponentName = component => component.displayName || component.name;

/**
 * Extract custom validation data, or return simple validation.
 * Example of simple validation: { age: { type: "number" } };
 * Example fo custom validation: { age: { type: { type: "number", message: "age must be a number" } }}
 */
const getValidationData = (rules, validationKey) => {
  let validation = null;
  let message = null;

  if (typeof rules[validationKey][validationKey] !== "undefined") {
    validation = rules[validationKey][validationKey];
  } else {
    validation = rules[validationKey];
  }

  if (typeof rules[validationKey].message !== "undefined") {
    message = rules[validationKey].message;

    // detect empty messages, to avoid unexpected scenarios.
    if (typeof message !== "string" || message.length === 0) {
      throw new Error(`Invalid message for validation key: ${validationKey}`);
    }
  }

  return { validation, message };
};

const validateEmail = value => {
  if (typeof value === "undefined") {
    return null;
  }

  // copied from https://stackoverflow.com/questions/46155/how-to-validate-an-email-address-in-javascript

  // eslint-disable-next-line no-useless-escape
  const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  if (!regex.test(String(value).toLowerCase())) {
    return "invalid email";
  }

  return null;
};

const assertCountries = props => {
  // Make sure we have a list of countries to look at. For address validation to work the props must contain an
  // array of countries like those returned from /api/countries.
  if (typeof props.countries === "undefined") {
    throw new Error("Address validation requires a list of countries from the component's props.");
  }
};

const findCountry = (model, props) => {
  assertCountries(props);

  const { countries } = props;

  // If there's no current value for country then let's skip validation. It's probably not currently set
  // so we can't tell if the state or city are required or not (yet).
  if (!model.country) {
    return null;
  }

  // For flexibility we'll assume any value is valid if the list of countries is empty.
  if (countries.length === 0) {
    return null;
  }

  return countries.find(country => country.id === model.country);
};

const validateCountry = (value, props) => {
  assertCountries(props);

  const { countries } = props;

  // if there's no value we won't validate it, that belongs to 'required' validation.
  if (!value) {
    return null;
  }

  // For flexibility we'll assume any value is valid if the list of countries is empty.
  if (countries.length === 0) {
    return null;
  }

  const country = countries.find(countryInLoop => countryInLoop.id === value);

  if (!country) {
    return "country not supported";
  }

  return null;
};

const validateState = (value, model, props) => {
  const country = findCountry(model, props);
  // If there's no matching country then let's skip validation. Country should be validated elsewhere, on it's own
  // "country" validation.
  if (!country) {
    return null;
  }

  // If no value provided, then we need to decide if to rise an error if state is required or allow the empty value.
  if (!value) {
    // Is the state required?
    if (country.stateRequired) {
      return "state is required";
    }

    // no error if no state given and state is not required.
    return null;
  }

  // From here we assume a value for state has been provided.

  // If for some reason a state is provided but the country has no states then we should not error. Making use
  // of the value or ignoring it should be handled elsewhere.
  if (!country.hasStates) {
    return null;
  }

  // If the country has a list of states, let's make sure the given value matches one of those states.
  const { states } = country;
  const flatStates = Array.isArray(states) ? states : Object.keys(states);

  // states is a plain array (support for BC)
  if (flatStates.indexOf(value) === -1) {
    return "invalid state";
  }

  return null;
};

const validateCity = (value, model, props) => {
  const country = findCountry(model, props);
  // If there's no matching country then let's skip validation. Country should be validated elsewhere, on it's own
  // "country" validation.
  if (!country) {
    return null;
  }

  // If no value provided, then we need to decide if to rise an error if state is required or allow the empty value.
  if (country.cityRequired && !value) {
    return "city is required";
  }

  return null;
};

const validateZip = (value, model, props) => {
  const country = findCountry(model, props);
  // If there's no matching country then let's skip validation. Country should be validated elsewhere, on it's own
  // "country" validation.
  if (!country) {
    return null;
  }

  // If no value provided, then we need to decide if to rise an error if state is required or allow the empty value.
  if (country.zipCodeRequired && !value) {
    return "zip is required";
  }

  // if zip code is optional and there's no value, then there's no validation violation
  if (!country.zipCodeRequired && !value) {
    return null;
  }

  if (!country.zipCodeRegex) {
    return null;
  }

  const withoutSlashes = country.zipCodeRegex.replace(/^\//, "").replace(/\/$/, "");
  const regex = new RegExp(withoutSlashes);

  if (!regex.test(value)) {
    return country.zipCodeErrorMessage;
  }

  return null;
};

const isBlank = value => {
  return value === null || value === undefined || String(value).trim() === "";
};

/**
 * @property {object} schema validation rules
 * @property {object} model
 * @property {object} props an object with validation dependencies like countries (when validating address related fields)
 * @property {object} backendErrors a representation of backend errors in the format { modelKey: "error message" }
 * If you need to validate the return value via PropTypes you can leverage the following snippet:
 * validation: PropTypes.shape({
    isValid: PropTypes.bool,
    // keys maps the model keys and the value is an object of shape { isValid: <bool>, errors: <Array> }
    fields: PropTypes.object,
  }),
 */
const getValidationErrors = (schema, model, props = {}, backendErrors = {}) => {
  const validationErrors = Object.keys(schema).reduce(
    (acc, key) => {
      const errors = [];
      const value = model[key];
      const rules = schema[key];

      const renderError = (condition, fallback) => {
        return typeof rules.formatError === "function"
          ? rules.formatError({ key, value, condition, rules, schema, model })
          : fallback;
      };

      if (rules.required) {
        const { validation: required, message } = getValidationData(rules, "required");

        if (required && isBlank(value)) {
          errors.push(renderError("required", message || `is required`));
        }
      }

      // eslint-disable-next-line valid-typeof
      if (rules.type) {
        const { validation: type, message } = getValidationData(rules, "type");

        // eslint-disable-next-line valid-typeof
        if (typeof value !== type) {
          errors.push(renderError("type", message || `must be of type ${type}, but got ${typeof value}`));
        }
      }

      if (rules.minLength) {
        const { validation: minLength, message } = getValidationData(rules, "minLength");

        if (!value || value.length < minLength) {
          errors.push(renderError("minLength", message || `must have at least ${minLength} characters`));
        }
      }

      if (rules.maxLength) {
        const { validation: maxLength, message } = getValidationData(rules, "maxLength");

        if (value && value.length > maxLength) {
          errors.push(renderError("maxLength", message || `must not have more than ${maxLength} characters`));
        }
      }

      // only validate INT if value is not blank
      if (rules.isInt && !isBlank(value)) {
        const { message } = getValidationData(rules, "isInt");
        const intValue = parseInt(value, 10);
        // Check if value is a number and not a decimal
        if (Number.isNaN(intValue) || Math.floor(value) - value !== 0) {
          errors.push(renderError("isInt", message || `must be an integer`));
        }
      }

      if (rules.test) {
        let error;
        rules.test(value, msg => {
          error = msg;
        });
        if (error) {
          errors.push(error);
        }
      }

      // Address validation
      if (rules.address) {
        const { validation: address, message } = getValidationData(rules, "address");
        let error = null;

        //  Country validation
        if (address === "country") {
          error = validateCountry(value, props);
        }

        // State validation. Each country record from the backend (/api/countries) has information about
        // if the state is required or not and all the states available.
        if (address === "state") {
          error = validateState(value, model, props);
        }

        if (address === "city") {
          error = validateCity(value, model, props);
        }

        if (address === "zip") {
          error = validateZip(value, model, props);
        }

        // if an error message was returned, it means the validation failed, let's add the message.
        if (error) {
          // prefer custom "message" (if any) over the static error message returned by the address validator.
          errors.push(message || error);
        }
      }

      // misc

      if (rules.email) {
        const error = validateEmail(value);

        if (error) {
          const { message } = getValidationData(rules, "email");
          errors.push(message || error);
        }
      }

      if (rules.callback) {
        // note that we don't use getValidationData() for this rule, it's because the callback will return
        // a custom error message anyways (if there's an error).
        const error = rules.callback(value, model, props);

        if (error) {
          errors.push(error);
        }
      }

      return assign({}, acc, {
        isValid: !errors.length && acc.isValid,
        fields: assign({}, acc.fields, {
          [key]: {
            isValid: !errors.length,
            errors,
          },
        }),
      });
    },
    { isValid: true, fields: {} },
  );

  const globalErrors = {};

  Object.keys(backendErrors).forEach(field => {
    // bind message only if property exists on the model, otherwise we'll add it to the global messages.
    const modelKeys = Object.keys(model);

    if (modelKeys.indexOf(field) === -1) {
      globalErrors[field] = backendErrors[field];
    } else {
      validationErrors.fields[field].errors.push(backendErrors[field]);
    }
  });

  validationErrors.globalErrors = globalErrors;

  return validationErrors;
};

/**
 * @type {Object} schema contain the data for validating an object.
 *       example: { age: { type: "number" } }
 *       a custom error message can be provided as well: { age: { type: { type: "number", message: "age must be a number" } } }
 */
const validateSchema = schema => WrappedComponent => {
  const validated = observer(props => {
    // eslint-disable-next-line react/destructuring-assignment, react/prop-types
    const validationErrors = getValidationErrors(schema, props.model, props);

    return React.createElement(
      WrappedComponent,
      assign({}, props, {
        schema: validationErrors,
      }),
    );
  });

  validated.displayName = `ValidateSchema(${getComponentName(WrappedComponent)})`;
  return hoistNonReactStatics(validated, WrappedComponent);
};

export default validateSchema;

export { validateEmail, getValidationErrors };
